import React from 'react';
import { Row, Col, Table, Card } from 'reactstrap';
import CodeBlock from '../CodeBlock';
import { HashLink as Link } from 'react-router-hash-link';

function EndpointSection() {
    return (
        <section id="endpoints">
            <Row>
                <Col>
                    <h2>Endpoints</h2>
                    <p>
                        Please note that all API calls (except calls to Dictionary endpoint) require a valid token to be included
                        in the request header (see the details in the Authorization Server section) as well as the client user id
                        provided.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Authorization</td>
                                <td>string</td>
                                <td>{`bearer {access_token}`}</td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>client_user_id</td>
                                <td>string</td>
                                <td>
                                    Internal API client user identifier{' '}
                                    <i>(user id or username from your database (same value as in authorization request))</i>
                                </td>
                                <td>
                                    <i>Optional</i>
                                </td>
                            </tr>
                            <tr>
                                <td>client_device_id</td>
                                <td>string</td>
                                <td>
                                    Unique browser identifier{' '}
                                    <i>
                                        (long lifespan cookie with random unique value can be created and this value can be used as this
                                        browser device identifier (same value as in authorization request).)
                                    </i>
                                </td>
                                <td>
                                    <i>Optional</i>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="search">Search</h3>
                    <p>
                        Search for candidates by sending a <code>POST</code> request to <code>/Search</code>.
                    </p>
                    <p>
                        Filter values are validated and validation messages are being returned and values for lookups (like
                        countries, languages, etc.) are exposed through dictionary endpoint <code>/Dictionary</code>
                    </p>
                    <h4 id="candidateactivity">Candidate&apos;s Activity</h4>
                    <p>
                        Current version of Search introduces a possibility of expanding candidate&apos;s data by their
                        applications summary. Full information regarding schema is available in application summary data
                        chapter, response section. Please refer to below parameters table for details regarding switching this
                        option on.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>includeCandidatesActivity</td>
                                <td>boolean</td>
                                <td>
                                    If set to true, enables Candidate&apos;s application summary data available in search results.{' '}
                                    <i>(Default value is false)</i>
                                </td>
                                <td>
                                    <i>Optional</i>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <p>
                        Example endpoint: <code>/Search?includeCandidatesActivity=true</code>
                    </p>
                    <h4 id="facets">Facets</h4>
                    <p>
                        Faceted search is a major extension to normal search mode. It provides new Facets object in result set.
                        Facets may be used to perform subsequent search requests, based on values they provide. For example and
                        details how to use facets please refer to Facets usage guide chapter. To switch Faceted search on, provide
                        the appropriate parameter in request URL. Please check Parameters table below.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>includeFacets</td>
                                <td>boolean</td>
                                <td>
                                    If set to true, enables Candidate&apos;s application summary data available in search results.{' '}
                                    <i>(Default value is false)</i>
                                </td>
                                <td>
                                    <i>Optional</i>
                                </td>
                            </tr>
                        </tbody>
                    </Table>
                    <p>
                        Example endpoint: <code>/Search?includeFacets=true</code>
                    </p>
                    <h4 id="usageType">Usage Type</h4>
                    <i>Optional</i>
                    <p>
                        Specify how search is being used, there are only two options allowed here <code>Auto</code> and{' '}
                        <code>Manual</code>
                    </p>
                    <Card className="bg-dark text-white" body>
                        <h4>USAGE TYPE REQUEST HEADER EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`UsageType: Auto`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="requestmodeldefinition">
                        Request Model Definition<small className="text-muted">(filters common for all brands)</small>
                    </h3>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>
                                    <Link to="#sort">Sort</Link>
                                </td>
                                <td>object</td>
                                <td>
                                    Defines whether the results should be displayed in an ascending or descending order and which
                                    column the sort should be applied against.
                                </td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>
                                    <Link to="#page">Page</Link>
                                </td>
                                <td>object</td>
                                <td>Defines the max number of candidates to be returned and the page number.</td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>LastActivityDate</td>
                                <td>Date</td>
                                <td>
                                    Required format <i>dd/MM/yyyy HH:mm:ss</i> only return candidates who have been active between now
                                    and the date provided.
                                </td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>SearchType</td>
                                <td>string</td>
                                <td>
                                    Specify which part of the candidates records to search against, there are only two options allowed
                                    here <code>JobTitle</code> and <code>ProfileAndCV</code>.
                                </td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>SearchOption</td>
                                <td>string</td>
                                <td>
                                    <p>There are only two options allowed here:</p>
                                    <p>
                                        <code>SmartSearch</code> - We take the given search term(s), tokenise and extrapolate the terms
                                        to broaden the search.
                                    </p>
                                    <p>
                                        <code>ProfileAndCV</code> - We use the exact search term(s) provided to perform the search.
                                    </p>
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>SearchText</td>
                                <td>string</td>
                                <td>Keywords to search against, this may also contain a boolean query.</td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>
                                    <Link to="#salary">Salary</Link>
                                </td>
                                <td>object</td>
                                <td>Defines the salary range and salary type.</td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>
                                    SalaryHour{' '}
                                    <i>
                                        (<Link to="#facetsusage">facet</Link>)
                                    </i>
                                </td>
                                <td>string array</td>
                                <td>
                                    Facet parameter, use one of values returned in <code>SalaryHour</code> facet.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>
                                    SalaryDay{' '}
                                    <i>
                                        (<Link to="#facetsusage">facet</Link>)
                                    </i>
                                </td>
                                <td>string array</td>
                                <td>
                                    Facet parameter, use one of values returned in <code>SalaryDay</code> facet.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>
                                    SalaryAnnual{' '}
                                    <i>
                                        (<Link to="#facetsusage">facet</Link>)
                                    </i>
                                </td>
                                <td>string array</td>
                                <td>
                                    Facet parameter, use one of values returned in <code>SalaryAnnual</code> facet.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>
                                    JobDescription{' '}
                                    <i>
                                        (<Link to="#facetsusage">facet</Link>)
                                    </i>
                                </td>
                                <td>string array</td>
                                <td>
                                    Facet parameter, use one of values returned in <code>JD</code> facet.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>
                                    Discipline{' '}
                                    <i>
                                        (<Link to="#facetsusage">facet</Link>)
                                    </i>
                                </td>
                                <td>string array</td>
                                <td>
                                    Facet parameter, use one of values returned in <code>Discipline</code> facet.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>Languages</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/Languages</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr className="table-danger">
                                <td>EducationLevel (Obsolete)</td>
                                <td>string</td>
                                <td>
                                    This has been replaced with <Link to="#highestlevelofeducation">HighestLevelOfEducation</Link>{' '}
                                    property. Allows values from the <code>/Dictionary/EducationLevel</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td id="highestlevelofeducation">HighestLevelOfEducation</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/EducationLevel</code> endpoint. This property is also a{' '}
                                    <Link to="#facetsusage">facet</Link>, values from <code>HighestLevelOfEducation</code> facet can
                                    also be used.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>Countries</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/Countries </code> endpoint. This property is also a{' '}
                                    <Link to="#facetsusage">facet</Link>, values from <code>Countries</code> facet can also be used.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr className="table-danger">
                                <td>Hour (Obsolete)</td>
                                <td>string</td>
                                <td>
                                    This has been replaced with <Link to="#jobhours">JobHours</Link> property. Allows values from the{' '}
                                    <code>/Dictionary/JobHours</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td id="jobhours">JobHours</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/JobHours</code> endpoint. This property is also a{' '}
                                    <Link to="#facetsusage">facet</Link>, values from <code>JobHours</code> facet can also be used.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>DesiredLocation</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/DesiredLocations</code> endpoint. <br />
                                    <b>Important note:</b> cannot be used together with <i>PostcodeDistance</i> filter.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>
                                    Towns{' '}
                                    <i>
                                        (<Link to="#facetsusage">facet</Link>)
                                    </i>
                                </td>
                                <td>string array</td>
                                <td>
                                    Facet parameter, use one of values returned in <code>Towns</code> facet.
                                    <br />
                                    <b>Important note:</b> Not available when searching using DesiredLocations.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr className="table-danger">
                                <td>
                                    <Link to="#postcodedistance">PostcodeDistance (Obsolete)</Link>
                                </td>
                                <td>object</td>
                                <td>
                                    <b>Important note:</b> cannot be used together with <i>DesiredLocation</i> filter.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td id="currentlocation">CurrentLocation</td>
                                <td>string</td>
                                <td>
                                    Allows values from the <code>/Dictionary/SearchableLocations</code> endpoint or properly formatted
                                    full postcodes or postcode out codes.
                                    <br />
                                    <b>Important note:</b> Should be used together with <Link to="#radius">Radius</Link>.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td id="radius">Radius</td>
                                <td>int</td>
                                <td>
                                    Distance expressed in miles. When a request is made with a radius in excess of 500 miles the value
                                    provided is replaced with 500 miles.
                                    <br />
                                    <b>Important note:</b> Should be used with <Link to="#currentlocation">CurrentLocation</Link>.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>HideCandidatesViewedSince</td>
                                <td>string</td>
                                <td>
                                    Allows values from the <code>/Dictionary/HideCandidatesViewed</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>WillingToRelocate</td>
                                <td>boolean</td>
                                <td>
                                    Include candidates who have not specified a desired location and are therefore assumed that they
                                    are willing to relocate.
                                    <br />
                                    <b>Important note:</b> Should be used with <Link to="#currentlocation">CurrentLocation</Link>.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>
                                    DrivingLicence{' '}
                                    <i>
                                        (<Link to="#facetsusage">facet</Link>)
                                    </i>
                                </td>
                                <td>string array</td>
                                <td>
                                    Whether the candidate holds a driving licence. Facet parameter, use one of values returned in{' '}
                                    <code>DrivingLicence</code> facet.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>
                                    GraduationYear{' '}
                                    <i>
                                        (<Link to="#facetsusage">facet</Link>)
                                    </i>
                                </td>
                                <td>string array</td>
                                <td>
                                    The candidates graduation year. Facet parameter, use one of values returned in{' '}
                                    <code>GraduationYear</code> facet.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>
                                    NoticePeriod{' '}
                                    <i>
                                        (<Link to="#facetsusage">facet</Link>)
                                    </i>
                                </td>
                                <td>string array</td>
                                <td>
                                    The candidates required notice period. Facet parameter, use one of values returned in{' '}
                                    <code>NoticePeriod</code> facet.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>
                                    <Link to="#targetedsearch">TargetedSearch</Link>
                                </td>
                                <td>object</td>
                                <td>Search for candidates based on job title and or skills.</td>
                                <td>false</td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="sort">Sort</h4>
                    <p>
                        The <code>Sort</code> object contains the following information.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Descending</td>
                                <td>boolean</td>
                                <td>
                                    If set to true will display defined column in descending order if false results will be in
                                    ascending order.
                                </td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>Column</td>
                                <td>string</td>
                                <td>
                                    Allows values from the <code>/Dictionary/SortColumns</code> endpoint.
                                </td>
                                <td>true</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`"Sort": {\n\t"Descending": true,\n\t"Column": "Relevancy"\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="page">Page</h4>
                    <p>
                        The <code>Page</code> object contains the following information.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>MaxRecords</td>
                                <td>integer</td>
                                <td>Max number of candidates to be returned.</td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>Page </td>
                                <td>integer</td>
                                <td>Results page to be returned.</td>
                                <td>true</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`"Page": {\n\t"MaxRecords": 10,\n\t"Page": 1\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="salary">Salary</h4>
                    <p>
                        The <code>Salary</code> object contains the following information.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>SalaryRateType</td>
                                <td>string</td>
                                <td>
                                    Allows values from the <code>/Dictionary/SalaryRateType</code> endpoint.
                                </td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>From</td>
                                <td>float</td>
                                <td>Salary min range (£).</td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>To</td>
                                <td>float</td>
                                <td>Salary max range (£).</td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>Unspecified</td>
                                <td>boolean</td>
                                <td>
                                    Optional property to include candidates with unspecified salaries. If not defined will default to{' '}
                                    <code>False</code>
                                </td>
                                <td>false</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`"Salary": {\n\t"SalaryRateType": "Daily Rate",\n\t"From": 10,\n\t"To": 200\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="postcodedistance">PostcodeDistance (Obsolete)</h4>
                    <p>
                        The <code>PostcodeDistance</code> object contains the following information.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>Postcode</td>
                                <td>string</td>
                                <td>Allows valid UK Postcodes.</td>
                                <td>true</td>
                            </tr>
                            <tr>
                                <td>Radius</td>
                                <td>float</td>
                                <td>
                                    Valid radius values are equal or greater than 0. Radius can be also set to -1 for UK wide search.
                                    Value is defined in miles. When a request is made with a radius in excess of 500 miles the value
                                    provided is replaced with 500 miles.
                                </td>
                                <td>true</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`"PostcodeDistance": {\n\t"Postcode": "SW1A 1AA",\n\t"Radius": 10.5\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4 id="targetedsearch">TargetedSearch</h4>
                    <p>
                        The <code>TargetedSearch</code> object contains the following information.
                    </p>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>JobTitleType</td>
                                <td>string</td>
                                <td>
                                    Allows values from the <code>/Dictionary/JobTitleType</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>SearchJobTitles</td>
                                <td>string</td>
                                <td>Keyword or boolean search string.</td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>SearchSkills</td>
                                <td>string</td>
                                <td>Keyword or boolean search string.</td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>SearchCvProfile</td>
                                <td>string</td>
                                <td>Keyword or boolean search string.</td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>CvProfileType</td>
                                <td>string</td>
                                <td>
                                    Allows values from the <code>/Dictionary/CvProfileType</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                        </tbody>
                    </Table>
                    <Card className="bg-dark text-white" body>
                        <h4>OBJECT EXAMPLE</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`"TargetedSearch": {\n\t"JobTitleType" : "Current",\n\t"SearchJobTitles" : "tester",\n\t"SearchCvProfile" : "developer",\n\t"CvProfileType" : "CvOnly",\n\t"SearchSkills" : "java"\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="requestmodeldefinitiontj">
                        Request Model Definition<small className="text-muted">(Total Jobs specific filters)</small>
                    </h3>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td>IndustrySectors</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/TotalJobsIndustrySectors</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr className="table-danger">
                                <td>JobType (Obsolete)</td>
                                <td>string</td>
                                <td>
                                    This has been replaced with <Link to="#jobtypeprofile">JobTypeProfile</Link> property. Allows
                                    values from the <code>/Dictionary/JobTypes</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td id="jobtypeprofile">JobTypeProfile</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/JobTypes</code> endpoint or <i>JobTypeProfile</i> facet
                                    values.
                                </td>
                                <td>false</td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="requestmodeldefinitionct">
                        Request Model Definition<small className="text-muted">(Caterer specific filters)</small>
                    </h3>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="table-danger">
                                <td>CatererPosition (Obsolete)</td>
                                <td>string</td>
                                <td>
                                    This has been replaced with <Link to="#catererpositions">CatererPositions</Link> property. Allows
                                    values from the <code>/Dictionary/JobTypes</code> endpoint or <i>JobTypeProfile</i> facet values.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td id="catererpositions">CatererPositions</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/JobTypes</code> endpoint or <i>JobTypeProfile</i> facet
                                    values.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>CatererjobTypes</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/CatererJobTypes</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td>CatererEmployerTypes</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/CatererEmployerTypes</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="requestmodeldefinitioncs">
                        Request Model Definition<small className="text-muted">(CareerStructure specific filters)</small>
                    </h3>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="table-danger">
                                <td>CareerStructureJobRole (Obsolete)</td>
                                <td>string</td>
                                <td>
                                    This has been replaced with <Link to="#catererstructurejobroles">CareerStructureJobRoles</Link>{' '}
                                    property. Allows values from the <code>/Dictionary/CareerStructureJobRoles</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td id="catererstructurejobroles">CareerStructureJobRoles</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/CareerStructureJobRoles</code> endpoint or{' '}
                                    <i>CareerStructureJobRole</i> facet values.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr className="table-danger">
                                <td>CareerStructureSectors (Obsolete)</td>
                                <td>string</td>
                                <td>
                                    This has been replaced with{' '}
                                    <Link to="#careerstructuresectorsfacet">CareerStructureSectorsFacet</Link> property. Allows values
                                    from the <code>/Dictionary/CareerStructureSectors</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td id="careerstructuresectorsfacet">CareerStructureSectorsFacet</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/CareerStructureSectors</code> endpoint or{' '}
                                    <i>CareerStructureSector</i> facet values.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr className="table-danger">
                                <td>JobType (Obsolete)</td>
                                <td>string</td>
                                <td>
                                    This has been replaced with <Link to="#jobtypeprofilecs">JobTypeProfile</Link> property. Allows
                                    values from the <code>/Dictionary/JobTypes</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td id="jobtypeprofilecs">JobTypeProfile</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/JobTypes</code> endpoint or <i>JobTypeProfile</i> facet
                                    values.
                                </td>
                                <td>false</td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="requestmodeldefinitioncw">
                        Request Model Definition<small className="text-muted">(CWJobs specific filters)</small>
                    </h3>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="table-danger">
                                <td>JobType (Obsolete)</td>
                                <td>string</td>
                                <td>
                                    This has been replaced with <Link to="#jobtypeprofilecw">JobTypeProfile</Link> property. Allows
                                    values from the <code>/Dictionary/JobTypes</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td id="jobtypeprofilecw">JobTypeProfile</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/JobTypes</code> endpoint or <i>JobTypeProfile</i> facet
                                    values.
                                </td>
                                <td>false</td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="requestmodeldefinitionrc">
                        Request Model Definition<small className="text-muted">(Retail Choice specific filters)</small>
                    </h3>
                    <Table bordered>
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Type</th>
                                <th>Description</th>
                                <th>Required</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="table-danger">
                                <td>JobType (Obsolete)</td>
                                <td>string</td>
                                <td>
                                    This has been replaced with <Link to="#jobtypeprofilerc">JobTypeProfile</Link> property. Allows
                                    values from the <code>/Dictionary/JobTypes</code> endpoint.
                                </td>
                                <td>false</td>
                            </tr>
                            <tr>
                                <td id="jobtypeprofilerc">JobTypeProfile</td>
                                <td>string array</td>
                                <td>
                                    Allows values from the <code>/Dictionary/JobTypes</code> endpoint or <i>JobTypeProfile</i> facet
                                    values.
                                </td>
                                <td>false</td>
                            </tr>
                        </tbody>
                    </Table>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h3 id="searchrequestexample">Search Request Example</h3>
                    <Card className="bg-dark text-white" body>
                        <h4>REQUEST HEADERS</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`curl -X POST \\\n'https://recruiter.totaljobs.com/Search?includeFacets=true' \\\n-H 'authorization: bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJyb2xlIjoiQ2FuZGlkYXRlIFNlYXJjaCBVc2VyIiwic2l0ZUlkIjoiNCIsInVuaXF1ZV9uYW1lIjoiMzM5MTc1YWQtMTEwNS00OWVkLTk3MzQtOTU0YzM5NzJjNTNjIiwiY2xpZW50SWQiOiIwMUQ0MzFCNy02REMwLTRERDEtQkYzMS0xRDQwNzlEMTI0QkEiLCJjbGllbnRVc2VySWQiOiJBIiwiaXNzIjoiUmVjcnVpdGVyIiwiYXVkIjoiYWxsIiwiZXhwIjoxNjE3MjcxODA3LCJuYmYiOjE2MTcyNzAwMDcsIlNzYVNlc3Npb25JZCI6ImNkODNjMTcyLThkZGUtNDEwNy1hNzRjLTY1NjlhMGU3YTQ2ZSJ9.gLBhEwpM5Ezh0PihC1ZCNdf4moZy2qNp1Y23NL_MYto' \\\n-H 'cache-control: no-cache' \\\n-H 'content-type: application/json' \\\n-d '{\n\t"SearchText":"developer",\n\t"Sort":{\n\t\t"Descending":true,\n\t\t"Column":"LastActivityDate"\n\t},\n\t"Page":{\n\t\t"MaxRecords":50,\n\t\t"Page":1\n\t},\n\t"SearchType":"ProfileAndCV",\n\t"LastActivityDate":"01/01/2000 00:00:00"\n}'\n`}
                        </CodeBlock>
                    </Card>
                    <Card className="bg-dark text-white" body>
                        <h4>REQUEST HEADERS</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`Authorization: bearer eyJ0eXAiOiJKV1QiLCJhbGciOiJIUzI1NiJ9.eyJyb2xlIjoiQ2FuZGlkYXRlIFNlYXJjaCBVc2VyIiwic2l0ZUlkIjoiNCIsInVuaXF1ZV9uYW1lIjoiMzM5MTc1YWQtMTEwNS00OWVkLTk3MzQtOTU0YzM5NzJjNTNjIiwiY2xpZW50SWQiOiIwMUQ0MzFCNy02REMwLTRERDEtQkYzMS0xRDQwNzlEMTI0QkEiLCJjbGllbnRVc2VySWQiOiJBIiwiaXNzIjoiUmVjcnVpdGVyIiwiYXVkIjoiYWxsIiwiZXhwIjoxNjE3MjcxODA3LCJuYmYiOjE2MTcyNzAwMDcsIlNzYVNlc3Npb25JZCI6ImNkODNjMTcyLThkZGUtNDEwNy1hNzRjLTY1NjlhMGU3YTQ2ZSJ9.gLBhEwpM5Ezh0PihC1ZCNdf4moZy2qNp1Y23NL_MYto\nContent-Type: application/json`}
                        </CodeBlock>
                    </Card>
                    <Card className="bg-dark text-white" body>
                        <h4>REQUEST BODY</h4>
                        <CodeBlock langauge="shell" theme="dark">
                            {`{\n\t"SearchText":"developer",\n\t"Sort":{\n\t\t"Descending":true,\n\t\t"Column":"LastActivityDate"\n\t},\n\t"Page":{\n\t\t"MaxRecords":50,\n\t\t"Page":1\n\t},\n\t"SearchType":"ProfileAndCV",\n\t"LastActivityDate":"01/01/2000 00:00:00"\n}`}
                        </CodeBlock>
                    </Card>
                </Col>
            </Row>
        </section>
    )
}

export default EndpointSection
